// Dimension provenance for catalog aircraft — row shape of the
// `catalog_dimension_provenance` view (migration 20260930000001) and the
// buckets the admin Catalog page filters on.
//
// Wording is deliberate: aircraft_research "verified" means two sources with
// different URLs agree — often Wikipedia + a web agent — NOT that the value was
// checked against the manufacturer. The labels below never say "verified".

export interface CatalogProvenance {
  catalog_id: string
  asset_code: string
  kind: 'own' | 'shared' | 'proxy'
  catalog_confidence: string | null
  match_method: string | null
  candidate_count: number
  research_identity_key: string | null
  /** NULL for non-admins (RLS on aircraft_research) as well as for unmatched rows. */
  research_confidence: string | null
  research_width_m: number | null
  research_length_m: number | null
  research_height_m: number | null
  width_drift_pct: number | null
  length_drift_pct: number | null
  source_types: string[] | null
  has_faa_acd: boolean
  faa_acd_agrees: boolean
  /** FAA-ACD carries both span and length (else the agreement is on one axis only). */
  faa_acd_both_axes: boolean
  has_primary: boolean
  primary_agrees: boolean
  primary_both_axes: boolean
}

export type ProvenanceSource = CatalogProvenance['kind']
export type ProvenanceAgreement = 'verified' | 'single_source' | 'conflict' | 'unverified' | 'no_research'
export type ProvenancePrimary = 'both' | 'faa_acd' | 'manufacturer_tcds' | 'differs' | 'none'

export const DRIFT_THRESHOLD_PCT = 5

export const SOURCE_LABEL: Record<ProvenanceSource, string> = {
  own: 'Own 3D model',
  shared: 'Shared model (same airframe)',
  proxy: 'Proxy (scaled from similar)',
}

export const AGREEMENT_LABEL: Record<ProvenanceAgreement, string> = {
  verified: '2+ sources agree',
  single_source: '1 source',
  conflict: 'Sources conflict',
  unverified: 'Unverified',
  no_research: 'No research',
}

export const PRIMARY_LABEL: Record<ProvenancePrimary, string> = {
  both: 'FAA-ACD + manufacturer/TCDS agree',
  faa_acd: 'FAA-ACD agrees',
  manufacturer_tcds: 'Manufacturer/TCDS agrees',
  differs: 'Primary source differs',
  none: 'No primary source',
}

export interface ProvenanceBucket {
  source: ProvenanceSource
  agreement: ProvenanceAgreement
  primary: ProvenancePrimary
  /** Catalog span or length differs from the matched research row by more than DRIFT_THRESHOLD_PCT. */
  drifted: boolean
}

const AGREEMENTS = new Set<ProvenanceAgreement>(['verified', 'single_source', 'conflict', 'unverified'])

export function provenanceBucket(p: CatalogProvenance): ProvenanceBucket {
  const agreement: ProvenanceAgreement =
    p.research_identity_key && p.research_confidence && AGREEMENTS.has(p.research_confidence as ProvenanceAgreement)
      ? (p.research_confidence as ProvenanceAgreement)
      : 'no_research'

  let primary: ProvenancePrimary
  if (p.faa_acd_agrees && p.primary_agrees) primary = 'both'
  else if (p.faa_acd_agrees) primary = 'faa_acd'
  else if (p.primary_agrees) primary = 'manufacturer_tcds'
  else if (p.has_faa_acd || p.has_primary) primary = 'differs'
  else primary = 'none'

  const drifted = [p.width_drift_pct, p.length_drift_pct].some(
    (d) => d != null && Math.abs(Number(d)) > DRIFT_THRESHOLD_PCT,
  )

  return { source: p.kind, agreement, primary, drifted }
}

/** Fetches every page of a PostgREST query. `max_rows` clamps any single response to 1 000. */
export async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => Promise<T[]>,
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = []
  for (let from = 0; ; from += pageSize) {
    const page = await fetchPage(from, from + pageSize - 1)
    all.push(...page)
    if (page.length < pageSize) break
  }
  return all
}
